import { useState } from "react";
import { Link } from "react-router-dom";
import "./Calendario.css";

type Visita = {
  id: string | number;
  ubicacion_generica: string;
  fecha: string; // Formato: YYYY-MM-DD
};

type CalendarioVisitasProps = {
  visitas: Visita[];
  obtenerRutaDetalle: (visita: Visita) => string;
  obtenerEstadoDetalle?: (visita: Visita) => Record<string, unknown>;
};

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function claveFecha(fecha: Date): string {
  const anio = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");

  return `${anio}-${mes}-${dia}`;
}

export default function CalendarioVisitas({
  visitas,
  obtenerRutaDetalle,
  obtenerEstadoDetalle,
}: CalendarioVisitasProps) {
  const hoy = new Date();

  const [mesVisible, setMesVisible] = useState(
    () => new Date(hoy.getFullYear(), hoy.getMonth(), 1),
  );

  const anio = mesVisible.getFullYear();
  const mes = mesVisible.getMonth();

  // getDay() empieza en domingo. Lo adaptamos para empezar en lunes.
  const primerDia = new Date(anio, mes, 1);
  const desplazamiento = (primerDia.getDay() + 6) % 7;

  // Seis semanas mantienen estable la altura al cambiar de mes.
  const fechas = Array.from(
    { length: 42 },
    (_, indice) => new Date(anio, mes, indice - desplazamiento + 1),
  );

  const visitasPorFecha = new Map<string, Visita[]>();

  for (const visita of visitas) {
    const grupo = visitasPorFecha.get(visita.fecha) ?? [];
    grupo.push(visita);
    visitasPorFecha.set(visita.fecha, grupo);
  }

  const tituloMes = mesVisible.toLocaleDateString("es-AR", {
    month: "long",
    year: "numeric",
  });

  function cambiarMes(cantidad: number) {
    setMesVisible(
      (actual) =>
        new Date(actual.getFullYear(), actual.getMonth() + cantidad, 1),
    );
  }

  function volverAHoy() {
    const ahora = new Date();
    setMesVisible(new Date(ahora.getFullYear(), ahora.getMonth(), 1));
  }

  return (
    <section className="calendario" aria-label="Calendario de visitas">
      <header className="calendario__encabezado">
        <h2 aria-live="polite">{tituloMes}</h2>

        <div className="calendario__controles">
          <button
            type="button"
            aria-label="Mes anterior"
            onClick={() => cambiarMes(-1)}
          >
            ‹
          </button>

          <button type="button" onClick={volverAHoy}>
            Hoy
          </button>

          <button
            type="button"
            aria-label="Mes siguiente"
            onClick={() => cambiarMes(1)}
          >
            ›
          </button>
        </div>
      </header>

      <div className="calendario__scroll">
        <div className="calendario__contenido">
          <div className="calendario__semana">
            {DIAS.map((dia) => (
              <div key={dia}>{dia}</div>
            ))}
          </div>

          <div className="calendario__grilla">
            {fechas.map((fecha) => {
              const clave = claveFecha(fecha);
              const esHoy = clave === claveFecha(hoy);
              const esOtroMes = fecha.getMonth() !== mes;
              const visitasDelDia = visitasPorFecha.get(clave) ?? [];

              return (
                <div
                  key={clave}
                  className={[
                    "calendario__dia",
                    esOtroMes ? "calendario__dia--otro-mes" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <time
                    dateTime={clave}
                    aria-current={esHoy ? "date" : undefined}
                    className={
                      esHoy
                        ? "calendario__numero calendario__numero--hoy"
                        : "calendario__numero"
                    }
                  >
                    {fecha.getDate()}
                  </time>

                  <ul className="calendario__visitas">
                    {visitasDelDia.map((visita) => (
                      <li key={visita.id}>
                        <Link
                          to={obtenerRutaDetalle(visita)}
                          state={obtenerEstadoDetalle?.(visita)}
                          className="calendario__visita"
                        >
                          {visita.ubicacion_generica}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
